import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from "@angular/forms";
import { ModalType } from "@components/modal/modal.component";
import { Changes } from "@interfaces/changes";
import {
  CnpjLookup,
  CnpjLookupAtividade,
  CnpjLookupNcmSugerido
} from "@interfaces/cnpj-lookup";
import { NfseCityAuth } from "@interfaces/nfse-city-auth";
import { Option } from "@interfaces/option";
import { PNCompany, RpsNumeracao } from "@interfaces/pn-company";
import { Store } from "@ngrx/store";
import { CepService } from "@services/cep.service";
import { CompanyService } from "@services/company.service";
import { IbgeService } from "@services/ibge.service";
import { ModalService } from "@services/modal.service";
import { NcmService } from "@services/ncm.service";
import { AppActions } from "@state/actions/app.actions";
import {
  selectCertificate,
  selectPNCompany,
  selectUserId
} from "@state/selectors/user.selectors";
import regimeTributarioOptions from "@static/regimeTributario.json";
import regimesTributariosEspeciaisOptions from "@static/regimeTributarioEspecial.json";
import tipoLogradouroOptions from "@static/tipoLogradouro.json";
import yesOrNotOptions from "@static/yesOrNot.json";
import { Subscription, debounceTime, distinctUntilChanged, skip } from "rxjs";
import {
  isValidCertificate,
  formatCertificateDate
} from "src/app/utils/certificate-validator.util";
import { v4 } from "uuid";

type DocumentoNumerado = "nfe" | "nfse" | "nfce";

// Versões do QR Code da NFC-e aceitas pelo PlugNotas (`nfce.config.impressao`).
export const NFCE_QRCODE_COM_CSC = 2;
export const NFCE_QRCODE_SEM_CSC = 3;

@Component({
  selector: "app-company-settings-tab",
  templateUrl: "./company-settings-tab.component.html",
  styleUrls: ["./company-settings-tab.component.scss"]
})
export class CompanySettingsTabComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Output() data = new EventEmitter();
  @Output() uploadLogo = new EventEmitter<FormData>();
  /** NCM sugerido pelo CNAE que o usuário quer parametrizar agora na aba NF-e. */
  @Output() cadastrarNcm = new EventEmitter<string>();
  @Input() certificate: string | null = null;
  @Input() loading = false;

  userId = this.store.selectSignal(selectUserId);
  company = this.store.selectSignal(selectPNCompany);
  cert = this.store.selectSignal(selectCertificate);

  certificateData: { formData: FormData; valid: boolean } | null = null;

  companyForm = this.createCompanyForm(this.buildFormValue(this.company()));

  // Fonte única dos valores do formulário: usada na criação e ao repopular
  // quando a empresa chega/é recarregada do PlugNotas.
  private buildFormValue(company: PNCompany | null | undefined) {
    const nfeNumeracao = company?.nfe?.config?.numeracao;
    const rpsNumeracao = company?.nfse?.config?.rps?.numeracao;
    const nfceNumeracao = company?.nfce?.config?.numeracao;

    return {
      userId: this.userId(),
      cpfCnpj: company?.cpfCnpj,
      inscricaoEstadual: company?.inscricaoEstadual,
      inscricaoMunicipal: company?.inscricaoMunicipal,
      razaoSocial: company?.razaoSocial,
      nomeFantasia: company?.nomeFantasia,
      simplesNacional: !!company?.simplesNacional,
      certificado: company?.certificado,
      regimeTributario: company?.regimeTributario?.toString(),
      regimeTributarioEspecial: company?.regimeTributarioEspecial?.toString(),
      endereco: {
        bairro: company?.endereco?.bairro,
        cep: company?.endereco?.cep,
        codigoCidade: company?.endereco?.codigoCidade,
        estado: company?.endereco?.estado,
        logradouro: company?.endereco?.logradouro,
        numero: company?.endereco?.numero,
        tipoLogradouro: company?.endereco?.tipoLogradouro,
        complemento: company?.endereco?.complemento,
        descricaoCidade: company?.endereco?.descricaoCidade
      },
      email: company?.email,
      nfe: {
        ativo: !!company?.nfe?.ativo,
        config: {
          producao: !!company?.nfe?.config?.producao,
          email: { envio: !!company?.nfe?.config?.email?.envio },
          csrt: {
            id: company?.nfe?.config?.csrt?.id ?? "",
            token: company?.nfe?.config?.csrt?.token ?? ""
          },
          numeracao: nfeNumeracao?.length
            ? [...nfeNumeracao]
            : [{ serie: 1, numero: 1 }],
          serie: (nfeNumeracao?.[0]?.serie ?? "1").toString(),
          numero: (nfeNumeracao?.[0]?.numero ?? "1").toString(),
          ultimoNumeroEmitido: ""
        }
      },
      nfse: {
        ativo: !!company?.nfse?.ativo,
        config: {
          producao: !!company?.nfse?.config?.producao,
          rps: {
            lote: company?.nfse?.config?.rps?.lote || 1,
            numeracao: rpsNumeracao?.length
              ? [...rpsNumeracao]
              : [{ serie: 1, numero: 1 }],
            serie: (rpsNumeracao?.[0]?.serie ?? "1").toString(),
            numero: (rpsNumeracao?.[0]?.numero ?? "1").toString(),
            ultimoNumeroEmitido: ""
          },
          prefeitura: {
            login: company?.nfse?.config?.prefeitura?.login,
            senha: company?.nfse?.config?.prefeitura?.senha
          },
          email: { envio: !!company?.nfse?.config?.email?.envio }
        }
      },
      nfce: {
        ativo: !!company?.nfce?.ativo,
        config: {
          producao: !!company?.nfce?.config?.producao,
          email: { envio: !!company?.nfce?.config?.email?.envio },
          // QR Code 3 (NT 2025.001) por padrão: dispensa o CSC.
          versaoQrCode: (
            company?.nfce?.config?.impressao?.versaoQrCode ??
            NFCE_QRCODE_SEM_CSC
          ).toString(),
          sefaz: {
            idCodigoSegurancaContribuinte:
              company?.nfce?.config?.sefaz?.idCodigoSegurancaContribuinte ?? "",
            codigoSegurancaContribuinte:
              company?.nfce?.config?.sefaz?.codigoSegurancaContribuinte ?? ""
          },
          numeracao: nfceNumeracao?.length
            ? [...nfceNumeracao]
            : [{ serie: 1, numero: 1 }],
          serie: (nfceNumeracao?.[0]?.serie ?? "1").toString(),
          numero: (nfceNumeracao?.[0]?.numero ?? "1").toString(),
          ultimoNumeroEmitido: ""
        }
      }
    };
  }

  private createCompanyForm(
    initial: ReturnType<CompanySettingsTabComponent["buildFormValue"]>
  ) {
    return new FormGroup({
      userId: new FormControl(initial.userId, [Validators.required]),
      cpfCnpj: new FormControl(initial.cpfCnpj, [Validators.required]),
      inscricaoEstadual: new FormControl(initial.inscricaoEstadual),
      inscricaoMunicipal: new FormControl(initial.inscricaoMunicipal),
      razaoSocial: new FormControl(initial.razaoSocial, [Validators.required]),
      nomeFantasia: new FormControl(initial.nomeFantasia),
      simplesNacional: new FormControl(initial.simplesNacional),
      certificado: new FormControl(initial.certificado),
      regimeTributario: new FormControl(initial.regimeTributario, [
        Validators.required
      ]),
      regimeTributarioEspecial: new FormControl(
        initial.regimeTributarioEspecial,
        [Validators.required]
      ),
      endereco: new FormGroup({
        bairro: new FormControl(initial.endereco.bairro),
        cep: new FormControl(initial.endereco.cep),
        codigoCidade: new FormControl(initial.endereco.codigoCidade),
        estado: new FormControl(initial.endereco.estado),
        logradouro: new FormControl(initial.endereco.logradouro),
        numero: new FormControl(initial.endereco.numero),
        tipoLogradouro: new FormControl(initial.endereco.tipoLogradouro),
        complemento: new FormControl(initial.endereco.complemento),
        descricaoCidade: new FormControl(initial.endereco.descricaoCidade)
      }),
      email: new FormControl(initial.email, [
        Validators.required,
        Validators.email
      ]),
      nfe: new FormGroup({
        ativo: new FormControl(initial.nfe.ativo),
        config: new FormGroup({
          producao: new FormControl(initial.nfe.config.producao),
          email: new FormGroup({
            envio: new FormControl(initial.nfe.config.email.envio)
          }),
          csrt: new FormGroup({
            id: new FormControl(initial.nfe.config.csrt.id),
            token: new FormControl(initial.nfe.config.csrt.token)
          }),
          numeracao: new FormControl<RpsNumeracao[]>(
            initial.nfe.config.numeracao
          ),
          serie: new FormControl<string>(initial.nfe.config.serie, [
            Validators.maxLength(5)
          ]),
          numero: new FormControl<string>(initial.nfe.config.numero),
          ultimoNumeroEmitido: new FormControl<string>(
            initial.nfe.config.ultimoNumeroEmitido
          )
        })
      }),
      nfse: new FormGroup({
        ativo: new FormControl(initial.nfse.ativo),
        config: new FormGroup({
          producao: new FormControl(initial.nfse.config.producao),
          rps: new FormGroup({
            lote: new FormControl(initial.nfse.config.rps.lote),
            numeracao: new FormControl<RpsNumeracao[]>(
              initial.nfse.config.rps.numeracao
            ),
            serie: new FormControl<string>(initial.nfse.config.rps.serie, [
              Validators.maxLength(5)
            ]),
            numero: new FormControl<string>(initial.nfse.config.rps.numero),
            ultimoNumeroEmitido: new FormControl<string>(
              initial.nfse.config.rps.ultimoNumeroEmitido
            )
          }),
          prefeitura: new FormGroup({
            login: new FormControl(initial.nfse.config.prefeitura.login),
            senha: new FormControl(initial.nfse.config.prefeitura.senha)
          }),
          email: new FormGroup({
            envio: new FormControl(initial.nfse.config.email.envio)
          })
        })
      }),
      nfce: new FormGroup({
        ativo: new FormControl(initial.nfce.ativo),
        config: new FormGroup({
          producao: new FormControl(initial.nfce.config.producao),
          email: new FormGroup({
            envio: new FormControl(initial.nfce.config.email.envio)
          }),
          versaoQrCode: new FormControl<string>(
            initial.nfce.config.versaoQrCode
          ),
          sefaz: new FormGroup({
            idCodigoSegurancaContribuinte: new FormControl(
              initial.nfce.config.sefaz.idCodigoSegurancaContribuinte
            ),
            codigoSegurancaContribuinte: new FormControl(
              initial.nfce.config.sefaz.codigoSegurancaContribuinte
            )
          }),
          numeracao: new FormControl<RpsNumeracao[]>(
            initial.nfce.config.numeracao
          ),
          serie: new FormControl<string>(initial.nfce.config.serie, [
            Validators.maxLength(5)
          ]),
          numero: new FormControl<string>(initial.nfce.config.numero),
          ultimoNumeroEmitido: new FormControl<string>(
            initial.nfce.config.ultimoNumeroEmitido
          )
        })
      })
    });
  }

  // Opções da versão do QR Code da NFC-e (docs.plugnotas.com.br, cadastro de
  // empresa): 3 = NT 2025.001, sem CSC; 2 = modelo anterior, exige o CSC da
  // SEFAZ do estado.
  nfceQrCodeOptions: Option[] = [
    {
      display: "Versão 3 (NT 2025.001) — sem CSC",
      value: NFCE_QRCODE_SEM_CSC.toString()
    },
    {
      display: "Versão 2 — exige CSC da SEFAZ",
      value: NFCE_QRCODE_COM_CSC.toString()
    }
  ];

  regimesTributarios: Option[] = regimeTributarioOptions;
  regimesTributariosEspeciais: Option[] = regimesTributariosEspeciaisOptions;
  yesOrNot: Option<boolean>[] = yesOrNotOptions;
  tipoLogradouroOptions: Option[] = tipoLogradouroOptions;

  states = signal<Option[]>([]);
  cities = signal<Option[]>([]);

  // Como a prefeitura do município da empresa autentica a NFS-e (metadados do
  // PlugNotas). O PlugNotas valida `nfse.config.prefeitura` por cidade e
  // descarta as credenciais que ela não usa; por isso só exibimos os campos
  // que o município utiliza. null = desconhecido/indisponível: mostra os dois
  // campos, como antes.
  nfseCityAuth = signal<NfseCityAuth | null>(null);
  private nfseCityAuthCodigo: string | null = null;

  // Última consulta do CNPJ na Receita (nf-server → PlugNotas). Alimenta o
  // resumo abaixo do CNPJ e a lista de NCMs sugeridos pelos CNAEs.
  cnpjLookup = signal<CnpjLookup | null>(null);
  cnpjLookupLoading = signal(false);
  private cnpjLookupCnpj: string | null = null;

  // Cidade vinda da consulta de CNPJ/CEP que ainda não pôde ser selecionada
  // porque a lista de municípios da UF ainda estava carregando.
  private cidadePendente: string | null = null;

  get cnpjSituacaoAtiva() {
    const lookup = this.cnpjLookup();
    return !lookup || lookup.situacaoCadastral === "ATIVA";
  }

  get cnpjAtividadePrincipal() {
    const lookup = this.cnpjLookup();
    return lookup?.atividades.find((a) => a.principal) ?? lookup?.atividades[0];
  }

  get razaoSocialExcedeLimite() {
    return (this.companyForm.get("razaoSocial")?.value ?? "").length > 60;
  }

  get nfeAtiva() {
    return !!this.nfe?.get("ativo")?.value;
  }

  /** NCMs servem à NF-e e à NFC-e: a seção de sugestões vale para as duas. */
  get produtoAtivo() {
    return this.nfeAtiva || this.nfceAtiva;
  }

  get ncmsSugeridos(): CnpjLookupNcmSugerido[] {
    return this.cnpjLookup()?.ncmsSugeridos ?? [];
  }

  get ncmsSugeridosPendentes() {
    return this.ncmsSugeridos.filter((s) => !s.jaCadastrado).length;
  }

  get nfseAtiva() {
    return !!this.nfse?.get("ativo")?.value;
  }

  get nfceAtiva() {
    return !!this.nfce?.get("ativo")?.value;
  }

  get nfce() {
    return this.companyForm.get("nfce");
  }

  get nfceConfig() {
    return this.nfce?.get("config");
  }

  get nfceNumeracaoLocked() {
    return !!this.company()?.nfce?.config?.numeracao?.length;
  }

  /** QR Code 2 exige o CSC (ID + código) obtido na SEFAZ do estado. */
  get nfceExigeCsc() {
    return (
      Number(this.nfceConfig?.get("versaoQrCode")?.value) ===
      NFCE_QRCODE_COM_CSC
    );
  }

  nfceAlterandoNumeracao = signal(false);

  /** Atividades (CNAEs) da empresa na Receita: cada uma vira um pré-cadastro de serviço ao salvar com NFS-e ativa. */
  get atividades(): CnpjLookupAtividade[] {
    return this.cnpjLookup()?.atividades ?? [];
  }

  get atividadesSemServico() {
    return this.atividades.filter((a) => !a.servicoCadastrado).length;
  }

  // Seleção de NCMs para "Importar selecionados como rascunho".
  ncmsSelecionados = signal<Set<string>>(new Set());
  importandoNcms = signal(false);

  get prefeituraUsaLogin() {
    const auth = this.nfseCityAuth();
    return !auth || auth.login;
  }

  get prefeituraUsaSenha() {
    const auth = this.nfseCityAuth();
    return !auth || auth.senha;
  }

  get prefeituraSomenteCertificado() {
    const auth = this.nfseCityAuth();
    return (
      !!auth && auth.certificado && !auth.login && !auth.senha && !auth.token
    );
  }

  get prefeituraAuthHint(): string {
    const auth = this.nfseCityAuth();
    if (!auth) return "";

    const cidade = [auth.nome, auth.uf].filter(Boolean).join("/");
    const local = cidade || "Este município";

    if (this.prefeituraSomenteCertificado) {
      return `${local} autentica a NFS-e apenas pelo certificado digital — a prefeitura não usa login e senha.`;
    }

    const credenciais = this.joinPortuguese(
      [
        auth.login ? "login" : "",
        auth.senha ? "senha" : "",
        auth.token ? "token" : ""
      ].filter(Boolean)
    );

    if (!credenciais) {
      return `${local} não exige credenciais da prefeitura.`;
    }

    return `${local} autentica a NFS-e com ${credenciais} da prefeitura${
      auth.certificado ? ", além do certificado digital" : ""
    }.`;
  }

  private joinPortuguese(items: string[]) {
    if (items.length <= 1) return items.join("");
    return `${items.slice(0, -1).join(", ")} e ${items[items.length - 1]}`;
  }

  get nfse() {
    return this.companyForm.get("nfse");
  }

  get nfe() {
    return this.companyForm.get("nfe");
  }

  get nfeConfig() {
    return this.nfe?.get("config");
  }

  // A numeração é sempre automática (orientação do contador): série e
  // número inicial só são informados na primeira configuração.
  // Travado = o PlugNotas já controla a numeração; a tela só exibe o atual.
  get nfeNumeracaoLocked() {
    return !!this.company()?.nfe?.config?.numeracao?.length;
  }

  get nfseConfig() {
    return this.nfse?.get("config");
  }

  get nfseRpsConfig() {
    return this.nfseConfig?.get("rps");
  }

  get nfseNumeracaoLocked() {
    return !!this.company()?.nfse?.config?.rps?.numeracao?.length;
  }

  // Botão "Alterar numeração": com a numeração já no PlugNotas, libera série
  // + número da última nota emitida (ex.: continuar a sequência de notas
  // emitidas em outra plataforma). Volta a travar ao cancelar ou ao recarregar
  // a empresa depois de salvar.
  nfeAlterandoNumeracao = signal(false);
  nfseAlterandoNumeracao = signal(false);

  get endereco() {
    return this.companyForm.get("endereco");
  }

  get certValid() {
    return isValidCertificate(this.cert()?.vencimento);
  }

  get certStatusText() {
    return this.certValid ? "Válido" : "Expirado";
  }

  get certFormattedDate() {
    return formatCertificateDate(this.cert()?.vencimento);
  }

  $: (Subscription | undefined)[] = [];

  constructor(
    private readonly store: Store,
    private readonly ibgeService: IbgeService,
    private readonly cepService: CepService,
    private readonly companyService: CompanyService,
    private readonly ncmService: NcmService,
    private readonly modalService: ModalService
  ) {
    this.$.push(
      this.companyForm.controls.endereco.controls.estado.valueChanges.subscribe(
        (state) => {
          this.fetchCities(state ?? "");
        }
      ),
      // A empresa pode chegar depois de a aba abrir e é recarregada do
      // PlugNotas após salvar: repopula o formulário, as cidades da UF e o
      // travamento/valor da numeração automática.
      toObservable(this.company).subscribe((company) => {
        const estadoAnterior = this.endereco?.get("estado")?.value;

        this.companyForm.patchValue(this.buildFormValue(company), {
          emitEvent: false
        });

        const estado = this.endereco?.get("estado")?.value;

        if (estado && estado !== estadoAnterior) {
          this.fetchCities(estado);
        }

        // Empresa (re)carregada — inclusive depois de salvar uma alteração
        // de numeração: encerra a edição e volta a travar os campos.
        this.setAlterandoNumeracao("nfe", false);
        this.setAlterandoNumeracao("nfse", false);
        this.setAlterandoNumeracao("nfce", false);
        // patchValue acima não emite eventos: sincroniza a cidade à mão.
        this.syncNfseCityAuth();
      }),
      this.companyForm.controls.endereco.controls.codigoCidade.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((codigoCidade) => {
          this.syncNfseCityAuth(codigoCidade);
        })
    );
  }

  ngOnInit(): void {
    this.syncNfseCityAuth();
    this.fetchStates();
    this.handleCepChange();
    this.handleCnpjChange();
    this.handleLogradouroChange();
    this.updateNfeNumeracaoControls();
    this.updateNfseNumeracaoControls();
    this.updateNfceNumeracaoControls();
  }

  ngOnChanges(changes: Changes<keyof CompanySettingsTabComponent>): void {
    if (changes.certificate)
      this.updateCertificate(changes.certificate.currentValue);
  }

  ngOnDestroy(): void {
    this.$.forEach(($) => {
      $?.unsubscribe();
    });
  }

  submit() {
    const nfeConfig = this.nfeConfig?.getRawValue() as {
      numeracao?: RpsNumeracao[];
      serie?: string;
      numero?: string;
      ultimoNumeroEmitido?: string;
    } | null;
    const nfseConfig = this.nfseConfig?.getRawValue() as {
      rps?: {
        lote?: number | string;
        numeracao?: RpsNumeracao[];
        serie?: string;
        numero?: string;
        ultimoNumeroEmitido?: string;
      };
    } | null;

    const nfeNumeracoes = nfeConfig?.numeracao ?? [];
    const serieNfe = (nfeConfig?.serie ?? "").toString().trim();
    const numeroNfe = (nfeConfig?.numero ?? "").toString().trim();
    const csrtId = (this.nfeConfig?.get("csrt.id")?.value ?? "")
      .toString()
      .trim();
    const csrtToken = (this.nfeConfig?.get("csrt.token")?.value ?? "")
      .toString()
      .trim();

    if ((csrtId && !csrtToken) || (!csrtId && csrtToken)) {
      this.showErrorModal(
        "Preencha os dois campos do CSRT (ID e Token) ou deixe ambos em branco."
      );
      return;
    }

    if (
      !this.validateNumeracao({
        locked: this.nfeNumeracaoLocked,
        serie: serieNfe,
        numero: numeroNfe,
        serieMessage:
          "A série da NF-e é obrigatória e não pode ter mais de 5 caracteres.",
        numeroMessage: "O número inicial da NF-e deve ser maior que zero."
      })
    ) {
      return;
    }

    const nfeAlteracaoNumeracao = this.buildAlteracaoNumeracao(
      "nfe",
      serieNfe,
      nfeConfig?.ultimoNumeroEmitido
    );

    if (nfeAlteracaoNumeracao === false) {
      return;
    }

    const normalizedNfeNumeracoes = this.normalizeNumeracoes({
      numeracoes: nfeNumeracoes,
      serie: serieNfe,
      numero: numeroNfe,
      locked: this.nfeNumeracaoLocked
    });

    const numeracoes = nfseConfig?.rps?.numeracao ?? [];
    const loteRpsRaw = (nfseConfig?.rps?.lote ?? "").toString().trim();
    const loteRps = Number(loteRpsRaw);
    const serieRps = (nfseConfig?.rps?.serie ?? "").toString().trim();
    const numeroRps = (nfseConfig?.rps?.numero ?? "").toString().trim();
    const nfseAtiva = !!this.nfse?.get("ativo")?.value;

    if (nfseAtiva && (!loteRpsRaw || Number.isNaN(loteRps) || loteRps < 1)) {
      this.showErrorModal("O lote da NFS-e deve ser um número maior que zero.");
      return;
    }

    if (
      !this.validateNumeracao({
        locked: this.nfseNumeracaoLocked,
        serie: serieRps,
        numero: numeroRps,
        serieMessage:
          "A série do RPS da NFS-e é obrigatória e não pode ter mais de 5 caracteres.",
        numeroMessage:
          "O número inicial do RPS da NFS-e deve ser maior que zero."
      })
    ) {
      return;
    }

    const nfseAlteracaoNumeracao = this.buildAlteracaoNumeracao(
      "nfse",
      serieRps,
      nfseConfig?.rps?.ultimoNumeroEmitido
    );

    if (nfseAlteracaoNumeracao === false) {
      return;
    }

    const normalizedNumeracoes = this.normalizeNumeracoes({
      numeracoes,
      serie: serieRps,
      numero: numeroRps,
      locked: this.nfseNumeracaoLocked
    });

    // ---- NFC-e -----------------------------------------------------------
    const nfceConfig = this.nfceConfig?.getRawValue() as {
      numeracao?: RpsNumeracao[];
      serie?: string;
      numero?: string;
      ultimoNumeroEmitido?: string;
      versaoQrCode?: string;
      sefaz?: {
        idCodigoSegurancaContribuinte?: string;
        codigoSegurancaContribuinte?: string;
      };
    } | null;
    const serieNfce = (nfceConfig?.serie ?? "").toString().trim();
    const numeroNfce = (nfceConfig?.numero ?? "").toString().trim();
    const cscId = (nfceConfig?.sefaz?.idCodigoSegurancaContribuinte ?? "")
      .toString()
      .trim();
    const cscCodigo = (nfceConfig?.sefaz?.codigoSegurancaContribuinte ?? "")
      .toString()
      .trim();

    if (this.nfceAtiva && this.nfceExigeCsc && (!cscId || !cscCodigo)) {
      this.showErrorModal(
        "Com o QR Code versão 2 é obrigatório informar o ID e o código do CSC (Código de Segurança do Contribuinte) obtidos na SEFAZ do estado, para o ambiente escolhido. Se preferir não usar CSC, selecione a versão 3 do QR Code."
      );
      return;
    }

    if (
      this.nfceAtiva &&
      !this.validateNumeracao({
        locked: this.nfceNumeracaoLocked,
        serie: serieNfce,
        numero: numeroNfce,
        serieMessage:
          "A série da NFC-e é obrigatória e não pode ter mais de 5 caracteres.",
        numeroMessage: "O número inicial da NFC-e deve ser maior que zero."
      })
    ) {
      return;
    }

    const nfceAlteracaoNumeracao = this.buildAlteracaoNumeracao(
      "nfce",
      serieNfce,
      nfceConfig?.ultimoNumeroEmitido
    );

    if (nfceAlteracaoNumeracao === false) {
      return;
    }

    const normalizedNfceNumeracoes = this.normalizeNumeracoes({
      numeracoes: nfceConfig?.numeracao ?? [],
      serie: serieNfce,
      numero: numeroNfce,
      locked: this.nfceNumeracaoLocked
    });

    const formData = this.companyForm.getRawValue();
    const nfeConfigWithoutHelpers = (
      formData.nfe?.config ? { ...formData.nfe.config } : {}
    ) as Record<string, unknown>;

    const csrtPayload = (
      formData.nfe?.config as {
        csrt?: { id?: string; token?: string };
      }
    )?.csrt;
    const csrtIdPayload = (csrtPayload?.id ?? "").toString().trim();
    const csrtTokenPayload = (csrtPayload?.token ?? "").toString().trim();

    if (!csrtIdPayload && !csrtTokenPayload) {
      delete nfeConfigWithoutHelpers["csrt"];
    } else {
      nfeConfigWithoutHelpers["csrt"] = {
        id: csrtIdPayload,
        token: csrtTokenPayload
      };
    }

    delete nfeConfigWithoutHelpers["serie"];
    delete nfeConfigWithoutHelpers["numero"];
    delete nfeConfigWithoutHelpers["numeracao"];
    delete nfeConfigWithoutHelpers["ultimoNumeroEmitido"];
    const rpsConfig = {
      ...(formData.nfse?.config?.rps ?? {})
    } as Record<string, unknown>;
    delete rpsConfig["serie"];
    delete rpsConfig["numero"];
    delete rpsConfig["numeracao"];
    delete rpsConfig["ultimoNumeroEmitido"];

    // NFC-e: campos auxiliares do formulário saem; versaoQrCode vira
    // `impressao.versaoQrCode` e o CSC só vai quando a versão 2 o exige.
    const nfceConfigPayload = (
      formData.nfce?.config ? { ...formData.nfce.config } : {}
    ) as Record<string, unknown>;
    const versaoQrCode =
      Number(nfceConfigPayload["versaoQrCode"]) || NFCE_QRCODE_SEM_CSC;
    delete nfceConfigPayload["serie"];
    delete nfceConfigPayload["numero"];
    delete nfceConfigPayload["numeracao"];
    delete nfceConfigPayload["ultimoNumeroEmitido"];
    delete nfceConfigPayload["versaoQrCode"];
    delete nfceConfigPayload["sefaz"];
    nfceConfigPayload["impressao"] = { versaoQrCode };
    if (versaoQrCode === NFCE_QRCODE_COM_CSC) {
      nfceConfigPayload["sefaz"] = {
        idCodigoSegurancaContribuinte: cscId,
        codigoSegurancaContribuinte: cscCodigo
      };
    }

    this.data.emit({
      formData: {
        ...formData,
        nfe: {
          ...formData.nfe,
          config: {
            ...nfeConfigWithoutHelpers,
            numeracaoAutomatica: true,
            ...(normalizedNfeNumeracoes
              ? { numeracao: normalizedNfeNumeracoes }
              : {}),
            ...(nfeAlteracaoNumeracao
              ? { alteracaoNumeracao: nfeAlteracaoNumeracao }
              : {})
          }
        },
        nfse: {
          ...formData.nfse,
          config: {
            ...formData.nfse?.config,
            rps: {
              ...rpsConfig,
              numeracaoAutomatica: true,
              lote: Number.isNaN(loteRps) ? 1 : loteRps,
              ...(normalizedNumeracoes
                ? { numeracao: normalizedNumeracoes }
                : {}),
              ...(nfseAlteracaoNumeracao
                ? { alteracaoNumeracao: nfseAlteracaoNumeracao }
                : {})
            }
          }
        },
        nfce: {
          ...formData.nfce,
          config: {
            ...nfceConfigPayload,
            numeracaoAutomatica: true,
            ...(normalizedNfceNumeracoes
              ? { numeracao: normalizedNfceNumeracoes }
              : {}),
            ...(nfceAlteracaoNumeracao
              ? { alteracaoNumeracao: nfceAlteracaoNumeracao }
              : {})
          }
        }
      },
      certificateData: this.certificateData
    });
  }

  handleCertificateData(data: { formData: FormData; valid: boolean }) {
    this.certificateData = data;
  }

  /**
   * Consulta no nf-server como a prefeitura do município autentica a NFS-e.
   * Só refaz a consulta quando o código IBGE muda; em erro, volta ao
   * comportamento antigo (exibir login e senha).
   */
  private syncNfseCityAuth(
    codigoCidade: unknown = this.endereco?.get("codigoCidade")?.value
  ) {
    const codigo = (codigoCidade ?? "").toString().replace(/\D/g, "");

    if (codigo.length !== 7) {
      this.nfseCityAuthCodigo = null;
      this.nfseCityAuth.set(null);
      return;
    }

    if (codigo === this.nfseCityAuthCodigo) return;

    this.nfseCityAuthCodigo = codigo;

    this.$.push(
      this.companyService.fetchNfseCityAuth(codigo).subscribe({
        next: (auth) => {
          if (this.nfseCityAuthCodigo === codigo) this.nfseCityAuth.set(auth);
        },
        error: () => {
          if (this.nfseCityAuthCodigo === codigo) this.nfseCityAuth.set(null);
        }
      })
    );
  }

  private updateCertificate(certificate: string | null) {
    if (!certificate) return;
    this.companyForm.get("certificado")?.setValue(certificate);
  }

  handleCepChange() {
    this.$.push(
      this.companyForm.controls.endereco.controls.cep.valueChanges
        .pipe(debounceTime(500), skip(1))
        .subscribe((cep) => {
          if (cep?.length !== 8) return;
          this.fetchCep(cep);
        })
    );
  }

  handleLogradouroChange() {
    this.$.push(
      this.companyForm.controls.endereco.controls.logradouro.valueChanges
        .pipe(debounceTime(500))
        .subscribe((logradouro) => {
          const option = this.tipoLogradouroOptions.find((o) =>
            logradouro
              ?.toLowerCase()
              .includes(o.value?.toString()?.toLowerCase())
          );

          if (option) {
            this.endereco
              ?.get("tipoLogradouro")
              ?.setValue(option?.value?.toString());
          }
        })
    );
  }

  handleCnpjChange() {
    this.$.push(
      this.companyForm.controls.cpfCnpj.valueChanges
        .pipe(debounceTime(500), skip(1))
        .subscribe((cnpj) => {
          const cnpjDigits = cnpj?.replace(/\D/g, "") || "";
          if (cnpjDigits.length !== 14) return;
          this.fetchCnpjLookup(cnpjDigits);
        })
    );
  }

  /**
   * Série/número inicial da NF-e: editáveis só até a primeira gravação; com a
   * numeração já no PlugNotas (sempre automática), os campos ficam travados
   * mostrando o número atual.
   */
  private updateNfeNumeracaoControls() {
    this.updateNumeracaoControls(
      this.nfeConfig?.get("serie"),
      this.nfeConfig?.get("numero"),
      this.company()?.nfe?.config?.numeracao?.[0]
    );
  }

  /** Idem para a NFC-e (série própria, modelo 65). */
  private updateNfceNumeracaoControls() {
    this.updateNumeracaoControls(
      this.nfceConfig?.get("serie"),
      this.nfceConfig?.get("numero"),
      this.company()?.nfce?.config?.numeracao?.[0]
    );
  }

  /** Idem para o RPS da NFS-e. */
  private updateNfseNumeracaoControls() {
    this.updateNumeracaoControls(
      this.nfseRpsConfig?.get("serie"),
      this.nfseRpsConfig?.get("numero"),
      this.company()?.nfse?.config?.rps?.numeracao?.[0]
    );
  }

  private updateNumeracaoControls(
    serieControl: AbstractControl | null | undefined,
    numeroControl: AbstractControl | null | undefined,
    pnNumeracao: RpsNumeracao | undefined
  ) {
    if (!serieControl || !numeroControl) return;

    if (pnNumeracao) {
      this.lockNumeracaoControls(serieControl, numeroControl, pnNumeracao);
    } else {
      serieControl.setValidators([Validators.maxLength(5)]);
      numeroControl.setValidators([Validators.min(1)]);
      serieControl.enable({ emitEvent: false });
      numeroControl.enable({ emitEvent: false });
      serieControl.markAsPristine();
      numeroControl.markAsPristine();
      serieControl.markAsUntouched();
      numeroControl.markAsUntouched();
    }

    serieControl.updateValueAndValidity({ emitEvent: false });
    numeroControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Botão "Alterar numeração". Pede confirmação antes de liberar os campos:
   * número menor que o já usado gera nota repetida (rejeitada) e número
   * pulado deixa lacuna na sequência.
   */
  iniciarAlteracaoNumeracao(documento: DocumentoNumerado) {
    const nome = this.nomeDocumento(documento);
    const lacuna =
      documento === "nfse"
        ? ""
        : ` Na ${nome}, números pulados precisam ser inutilizados na SEFAZ.`;

    this.modalService.confirm(
      `Use esta opção só para continuar a numeração de onde parou — por exemplo, se a empresa emitia ${nome} em outra plataforma. ` +
        `Informe a série e o número da ÚLTIMA ${nome} emitida: a próxima sai com o número seguinte. ` +
        `Um número menor que o já usado gera nota repetida, e pular números deixa uma lacuna na sequência.${lacuna}`,
      () => this.setAlterandoNumeracao(documento, true),
      undefined,
      {
        title: `Alterar numeração da ${nome}`,
        confirmText: "Alterar numeração",
        cancelText: "Cancelar"
      }
    );
  }

  cancelarAlteracaoNumeracao(documento: DocumentoNumerado) {
    this.setAlterandoNumeracao(documento, false);
  }

  private nomeDocumento(documento: DocumentoNumerado) {
    return documento === "nfe"
      ? "NF-e"
      : documento === "nfce"
        ? "NFC-e"
        : "NFS-e";
  }

  private setAlterandoNumeracao(documento: DocumentoNumerado, ativo: boolean) {
    const group: AbstractControl | null | undefined =
      documento === "nfe"
        ? this.nfeConfig
        : documento === "nfce"
          ? this.nfceConfig
          : this.nfseRpsConfig;
    const alterando =
      documento === "nfe"
        ? this.nfeAlterandoNumeracao
        : documento === "nfce"
          ? this.nfceAlterandoNumeracao
          : this.nfseAlterandoNumeracao;
    const serieControl = group?.get("serie");
    const ultimoControl = group?.get("ultimoNumeroEmitido");

    alterando.set(ativo);

    if (ultimoControl) {
      ultimoControl.setValue("", { emitEvent: false });
      ultimoControl.setValidators(
        ativo ? [Validators.required, Validators.min(0)] : []
      );
      ultimoControl.markAsUntouched();
      ultimoControl.updateValueAndValidity({ emitEvent: false });
    }

    if (ativo && serieControl) {
      serieControl.setValidators([
        Validators.required,
        Validators.maxLength(5)
      ]);
      serieControl.enable({ emitEvent: false });
      serieControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    // Fora da edição: série/número voltam ao estado do PlugNotas (travados).
    if (documento === "nfe") this.updateNfeNumeracaoControls();
    else if (documento === "nfce") this.updateNfceNumeracaoControls();
    else this.updateNfseNumeracaoControls();
  }

  /**
   * Pedido de alteração enviado ao nf-server, que converte "última nota
   * emitida" no número que o PlugNotas espera. null = sem alteração;
   * false = dado inválido (o erro já foi exibido).
   */
  private buildAlteracaoNumeracao(
    documento: DocumentoNumerado,
    serieRaw: string,
    ultimoRaw: string | undefined
  ): { serie: string; ultimoNumeroEmitido: number } | null | false {
    const alterando =
      documento === "nfe"
        ? this.nfeAlterandoNumeracao()
        : documento === "nfce"
          ? this.nfceAlterandoNumeracao()
          : this.nfseAlterandoNumeracao();
    const ativo =
      documento === "nfe"
        ? this.nfeAtiva
        : documento === "nfce"
          ? this.nfceAtiva
          : this.nfseAtiva;

    if (!alterando || !ativo) return null;

    const nome = this.nomeDocumento(documento);
    const serie = (serieRaw ?? "").toString().trim();
    const ultimoTexto = (ultimoRaw ?? "").toString().trim();
    const ultimo = Number(ultimoTexto);

    if (!serie || serie.length > 5) {
      this.showErrorModal(
        `Informe a série da ${nome} (até 5 caracteres) para alterar a numeração.`
      );
      return false;
    }

    if (!ultimoTexto || !Number.isInteger(ultimo) || ultimo < 0) {
      this.showErrorModal(
        `Informe o número da última ${nome} emitida (use 0 se nenhuma foi emitida nesta série).`
      );
      return false;
    }

    return { serie, ultimoNumeroEmitido: ultimo };
  }

  private lockNumeracaoControls(
    serieControl: AbstractControl,
    numeroControl: AbstractControl,
    pnNumeracao: RpsNumeracao
  ) {
    serieControl.clearValidators();
    numeroControl.clearValidators();
    serieControl.setValue((pnNumeracao.serie ?? "").toString(), {
      emitEvent: false
    });
    numeroControl.setValue(
      (pnNumeracao.numeracaoAtual ?? pnNumeracao.numero ?? "").toString(),
      { emitEvent: false }
    );
    serieControl.disable({ emitEvent: false });
    numeroControl.disable({ emitEvent: false });
  }

  private validateNumeracao({
    locked,
    serie,
    numero,
    serieMessage,
    numeroMessage
  }: {
    locked: boolean;
    serie: string;
    numero: string;
    serieMessage: string;
    numeroMessage: string;
  }) {
    if (locked) {
      return true;
    }

    // Numeração sempre automática: série/número inicial são opcionais (o
    // nf-server assume "1"/1); se o usuário informar, têm que ser válidos.
    const hasAnyValue = !!serie || !!numero;

    if (!hasAnyValue) {
      return true;
    }

    if (!serie || serie.length > 5) {
      this.showErrorModal(serieMessage);
      return false;
    }

    if (!numero || (Number(numero) || 0) < 1) {
      this.showErrorModal(numeroMessage);
      return false;
    }

    return true;
  }

  private normalizeNumeracoes({
    numeracoes,
    serie,
    numero,
    locked
  }: {
    numeracoes: RpsNumeracao[];
    serie: string;
    numero: string;
    locked: boolean;
  }) {
    // Numeração já controlada pelo PlugNotas: não reenviar, senão o contador
    // é redefinido a cada salvamento das configurações.
    if (locked) {
      return undefined;
    }

    const hasAnyValue = !!serie || !!numero;

    if (!hasAnyValue) {
      return undefined;
    }

    const normalizedNumero = Number(numero) || 1;

    return numeracoes.length
      ? numeracoes.map((n, index) =>
          index === 0
            ? {
                ...n,
                serie,
                numero: normalizedNumero
              }
            : n
        )
      : [
          {
            serie,
            numero: normalizedNumero
          }
        ];
  }

  private showErrorModal(message: string) {
    this.store.dispatch(
      AppActions.queueModal({
        modal: {
          title: "Erro",
          message,
          type: ModalType.ERROR,
          id: v4()
        }
      })
    );
  }

  fetchStates() {
    this.$.push(
      this.ibgeService.fetchStates().subscribe({
        next: (states) => {
          this.states.set(states);

          const state = this.endereco?.get("estado")?.value ?? "";

          if (state) {
            this.fetchCities(state);
          }
        }
      })
    );
  }

  fetchCities(uf: string) {
    this.$.push(
      this.ibgeService.fetchCities(uf).subscribe({
        next: (cities) => {
          this.cities.set(cities);
          this.applyCidadePendente();
        }
      })
    );
  }

  /**
   * O select de cidade só aceita o nome exatamente como o IBGE devolve. A
   * Receita manda "MARINGA" e o CEP "Maringá": casa sem acento/caixa e usa o
   * valor da opção.
   */
  private applyCidadePendente() {
    const pendente = this.cidadePendente;
    if (!pendente) return;

    const alvo = this.normalizeText(pendente);
    const option = this.cities().find(
      (city) => this.normalizeText(String(city.value ?? "")) === alvo
    );

    if (!option) return;

    this.cidadePendente = null;
    this.endereco?.get("descricaoCidade")?.setValue(String(option.value));
  }

  private normalizeText(value: string) {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  }

  fetchCep(cep: string) {
    if (cep.length !== 8) return;

    this.$.push(
      this.cepService.fetchCep(cep).subscribe({
        next: (address) => {
          this.endereco?.get("bairro")?.setValue(address.bairro);
          this.endereco?.get("logradouro")?.setValue(address.logradouro);
          this.endereco?.get("codigoCidade")?.setValue(address.ibge);
          this.endereco?.get("estado")?.setValue(address.uf);
          this.endereco?.get("descricaoCidade")?.setValue(address.municipio);
        },
        error: (err) => {
          this.store.dispatch(
            AppActions.queueModal({
              modal: {
                title: "Erro",
                message: err.error.error.message,
                type: ModalType.ERROR,
                id: v4()
              }
            })
          );
        }
      })
    );
  }

  get cnpjCompleto() {
    return (
      (this.companyForm.get("cpfCnpj")?.value ?? "")
        .toString()
        .replace(/\D/g, "").length === 14
    );
  }

  /** Ícone ao lado do CNPJ: força nova consulta (ex.: empresa já cadastrada). */
  buscarCnpj() {
    if (!this.cnpjCompleto || this.cnpjLookupLoading()) return;

    this.fetchCnpjLookup(
      (this.companyForm.get("cpfCnpj")?.value ?? "").toString(),
      true
    );
  }

  /**
   * Consulta automática ao completar um CNPJ válido (14 dígitos). O mesmo
   * CNPJ não é consultado duas vezes (salvo `force`); após erro, redigitar
   * dispara de novo.
   */
  fetchCnpjLookup(cnpj: string, force = false) {
    const cnpjDigits = cnpj.replace(/\D/g, "");

    if (!force && cnpjDigits === this.cnpjLookupCnpj) return;

    this.cnpjLookupCnpj = cnpjDigits;
    this.cnpjLookupLoading.set(true);

    this.$.push(
      this.companyService.fetchCnpj(cnpjDigits).subscribe({
        next: (lookup) => {
          // Resposta de um CNPJ que já foi trocado: ignora.
          if (this.cnpjLookupCnpj !== cnpjDigits) return;

          this.cnpjLookupLoading.set(false);
          this.cnpjLookup.set(lookup);
          this.ncmsSelecionados.set(new Set());
          this.applyCnpjLookup(lookup);
        },
        error: (err) => {
          if (this.cnpjLookupCnpj === cnpjDigits) {
            // Libera nova tentativa automática ao corrigir/redigitar o CNPJ.
            this.cnpjLookupCnpj = null;
            this.cnpjLookupLoading.set(false);
            this.cnpjLookup.set(null);
          }

          this.store.dispatch(
            AppActions.queueModal({
              modal: {
                title: "Erro",
                message:
                  err?.error?.error?.message ||
                  err?.error?.message ||
                  err?.message ||
                  "Não foi possível consultar o CNPJ informado.",
                type: ModalType.ERROR,
                id: v4()
              }
            })
          );
        }
      })
    );
  }

  /**
   * Preenche o formulário com o que a Receita devolveu. Só sobrescreve com
   * valor não vazio; IE, IM, regime e Simples continuam manuais
   * (`lookup.camposManuais`).
   */
  private applyCnpjLookup(lookup: CnpjLookup) {
    this.setIfPresent(this.companyForm.get("razaoSocial"), lookup.razaoSocial);
    this.setIfPresent(
      this.companyForm.get("nomeFantasia"),
      lookup.nomeFantasia
    );
    this.setIfPresent(this.companyForm.get("email"), lookup.email);

    const endereco = lookup.endereco;
    if (!endereco) return;

    // O nf-server já resolveu o CEP (IBGE/cidade/UF): não dispara a consulta
    // de CEP do formulário de novo.
    this.setIfPresent(this.endereco?.get("cep"), endereco.cep, {
      emitEvent: false
    });
    this.setIfPresent(this.endereco?.get("logradouro"), endereco.logradouro);
    this.setIfPresent(
      this.endereco?.get("tipoLogradouro"),
      endereco.tipoLogradouro
    );
    this.setIfPresent(this.endereco?.get("numero"), endereco.numero);
    this.setIfPresent(this.endereco?.get("complemento"), endereco.complemento);
    this.setIfPresent(this.endereco?.get("bairro"), endereco.bairro);
    this.setIfPresent(
      this.endereco?.get("codigoCidade"),
      endereco.codigoCidade
    );

    if (endereco.estado) {
      this.cidadePendente = endereco.descricaoCidade || null;

      const estadoAtual = this.endereco?.get("estado")?.value;
      this.endereco?.get("estado")?.setValue(endereco.estado);

      // Mesma UF: valueChanges não recarrega a lista, então casa a cidade já.
      if (estadoAtual === endereco.estado) this.applyCidadePendente();
    }
  }

  private setIfPresent(
    control: AbstractControl | null | undefined,
    value: string | null | undefined,
    options?: { emitEvent?: boolean }
  ) {
    if (!control) return;
    if (value === null || value === undefined || value === "") return;
    control.setValue(value, options);
  }

  solicitarCadastroNcm(sugestao: CnpjLookupNcmSugerido) {
    if (sugestao.jaCadastrado) return;
    this.cadastrarNcm.emit(sugestao.ncm);
  }

  // ---- Importação em lote como rascunho ----------------------------------

  ncmSelecionado(ncm: string) {
    return this.ncmsSelecionados().has(ncm);
  }

  toggleNcm(sugestao: CnpjLookupNcmSugerido, checked: boolean) {
    if (sugestao.jaCadastrado) return;
    this.ncmsSelecionados.update((atual) =>
      this.toggleInSet(atual, sugestao.ncm, checked)
    );
  }

  selecionarTodosNcms(checked: boolean) {
    this.ncmsSelecionados.set(
      new Set(
        checked
          ? this.ncmsSugeridos.filter((s) => !s.jaCadastrado).map((s) => s.ncm)
          : []
      )
    );
  }

  private toggleInSet(atual: Set<string>, valor: string, checked: boolean) {
    const proximo = new Set(atual);
    if (checked) proximo.add(valor);
    else proximo.delete(valor);
    return proximo;
  }

  /**
   * Cria os NCMs selecionados só com o código (sem tributos). O nf-server
   * ignora os já cadastrados e a emissão bloqueia até a parametrização.
   */
  importarNcmsSelecionados() {
    const ncms = [...this.ncmsSelecionados()];
    if (!ncms.length || this.importandoNcms()) return;

    this.importandoNcms.set(true);

    this.$.push(
      this.ncmService.importar(ncms).subscribe({
        next: (resultado) => {
          this.importandoNcms.set(false);
          this.ncmsSelecionados.set(new Set());
          this.marcarNcmsCadastrados(ncms);
          this.showSuccessModal(
            this.mensagemImportacao(
              "NCM",
              resultado.criados.length,
              resultado.ignorados.length,
              "Acesse a aba NF-e para parametrizar os tributos de cada um."
            )
          );
        },
        error: (err) => {
          this.importandoNcms.set(false);
          this.showErrorModal(
            this.extractErrorMessage(err, "Não foi possível importar os NCMs.")
          );
        }
      })
    );
  }

  private mensagemImportacao(
    tipo: string,
    criados: number,
    ignorados: number,
    proximoPasso: string
  ) {
    const partes = [
      `${criados} ${tipo}${criados === 1 ? "" : "s"} importado${
        criados === 1 ? "" : "s"
      } como rascunho.`
    ];
    if (ignorados > 0) {
      partes.push(`${ignorados} já cadastrado${ignorados === 1 ? "" : "s"}.`);
    }
    partes.push(proximoPasso);
    return partes.join(" ");
  }

  private marcarNcmsCadastrados(ncms: string[]) {
    const lookup = this.cnpjLookup();
    if (!lookup) return;
    const set = new Set(ncms);
    this.cnpjLookup.set({
      ...lookup,
      ncmsSugeridos: lookup.ncmsSugeridos.map((s) =>
        set.has(s.ncm) ? { ...s, jaCadastrado: true } : s
      )
    });
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    const e = err as {
      error?: { message?: string | string[]; error?: { message?: string } };
      message?: string;
    };
    const candidates = [
      e?.error?.error?.message,
      e?.error?.message,
      e?.message
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length) {
        return candidate.join("\n");
      }
      if (typeof candidate === "string" && candidate.trim()) return candidate;
    }

    return fallback;
  }

  private showSuccessModal(message: string) {
    this.store.dispatch(
      AppActions.queueModal({
        modal: {
          title: "Sucesso",
          message,
          type: ModalType.SUCCESS,
          id: v4()
        }
      })
    );
  }
}
